import { GridColDef } from "@mui/x-data-grid";
import { Stack, Typography, Tooltip, IconButton } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { LightTooltip } from "@/components/LightTooltip";
import { TrendIcon } from "./TrendIcon";
import { formatLocation } from "./utils/formatters";
import { INTENT_TABLE_SORT_FIELDS } from "./constants";
import { IntentTooltipContent } from "@/lib/company-filter/modules/intent/components";
import {
  BUYERS_JOURNEY_TOOLTIP_SECTIONS,
  INTENT_CONTEXT_TOOLTIP_SECTIONS,
} from "@/lib/company-filter/modules/intent/constants";
import type { IntentSignal } from "./types";

/**
 * Returns column definitions for the Intent Activity table
 * All columns are sortable with server-side sorting
 */
export const getIntentColumns = (): GridColDef<IntentSignal>[] => [
  // Column 1: Signal Strength with Trend Icon
  {
    field: INTENT_TABLE_SORT_FIELDS.SIGNAL_STRENGTH,
    headerName: "Signal Strength",
    minWidth: 125,
    flex: 0.8,
    sortable: true,
    renderCell: (params) => (
      <Stack direction="row" alignItems="center">
        <Typography>{params.row.score}</Typography>
        <TrendIcon
          trend={params.row.trend}
          topicName={params.row.topic.displayName}
        />
      </Stack>
    ),
  },

  // Column 2: Topic Name
  {
    field: INTENT_TABLE_SORT_FIELDS.TOPIC,
    headerName: "Topic",
    minWidth: 200,
    flex: 1.5,
    sortable: true,
    valueGetter: (_value, row) => row.topic.displayName,
  },

  // Column 3: Context Types (with null safety)
  {
    field: INTENT_TABLE_SORT_FIELDS.CONTEXT_TYPES,
    headerName: "Context",
    minWidth: 200,
    flex: 1.5,
    sortable: true,
    renderHeader: () => (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="subtitle2" fontWeight="bold">
          Context
        </Typography>
        <LightTooltip
          placement="bottom"
          title={
            <IntentTooltipContent
              sections={INTENT_CONTEXT_TOOLTIP_SECTIONS}
              maxWidth={320}
            />
          }
        >
          <IconButton
            size="small"
            aria-label="Intent context help"
            disableRipple
            sx={{ p: 0.25 }}
          >
            <HelpOutlineIcon fontSize="inherit" sx={{ fontSize: 16 }} />
          </IconButton>
        </LightTooltip>
      </Stack>
    ),
    valueGetter: (_value, row) =>
      row.contextAggregate?.typeNames?.join(", ") ?? "",
    renderCell: (params) => {
      const contextText =
        params.row.contextAggregate?.typeNames?.join(", ") ?? "";
      return (
        <Tooltip title={contextText}>
          <Typography noWrap>{contextText}</Typography>
        </Tooltip>
      );
    },
  },

  // Column 4: Buyer's Journey
  {
    field: INTENT_TABLE_SORT_FIELDS.BUYERS_JOURNEY,
    headerName: "Buyer's Journey",
    minWidth: 200,
    flex: 1.5,
    sortable: true,
    renderHeader: () => (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="subtitle2" fontWeight="bold">
          Buyer's Journey
        </Typography>
        <LightTooltip
          placement="bottom"
          title={
            <IntentTooltipContent
              sections={BUYERS_JOURNEY_TOOLTIP_SECTIONS}
              maxWidth={300}
            />
          }
        >
          <IconButton
            size="small"
            aria-label="Buyer's journey help"
            disableRipple
            sx={{ p: 0.25 }}
          >
            <HelpOutlineIcon fontSize="inherit" sx={{ fontSize: 16 }} />
          </IconButton>
        </LightTooltip>
      </Stack>
    ),
    valueGetter: (_value, row) => row.buyersJourney.name,
  },

  // Column 5: URL (with tooltip)
  {
    field: INTENT_TABLE_SORT_FIELDS.URL,
    headerName: "URL",
    minWidth: 125,
    flex: 1.2,
    sortable: true,
    renderCell: (params) => (
      <Tooltip title={params.row.url}>
        <Typography noWrap>{params.row.url}</Typography>
      </Tooltip>
    ),
  },

  // Column 6: Location (formatted with tooltip)
  {
    field: INTENT_TABLE_SORT_FIELDS.LOCATION,
    headerName: "Location",
    minWidth: 200,
    flex: 2,
    sortable: true,
    valueGetter: (_value, row) => formatLocation(row.location),
    renderCell: (params) => {
      const locationText = formatLocation(params.row.location);
      return (
        <Tooltip title={locationText}>
          <Typography noWrap>{locationText}</Typography>
        </Tooltip>
      );
    },
  },

  // Column 7: Location Strength
  {
    field: INTENT_TABLE_SORT_FIELDS.LOCATION_STRENGTH,
    headerName: "Location Strength",
    minWidth: 130,
    flex: 0.8,
    sortable: true,
    valueGetter: (_value, row) => row.locationScore,
  },

  // Column 8: Datestamp (ISO format from backend)
  {
    field: INTENT_TABLE_SORT_FIELDS.DATESTAMP,
    headerName: "Datestamp",
    minWidth: 125,
    flex: 0.8,
    sortable: true,
    valueGetter: (_value, row) => row.lastSeenAt,
  },
];
